import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './EmailVerificationPage.css';

const EmailVerificationPage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState(null);
  const [canResend, setCanResend] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail, resendVerificationCode } = useAuth();

  useEffect(() => {
    // Получаем параметры из URL
    const emailParam = searchParams.get('email');
    const userIdParam = searchParams.get('userId');
    const codeParam = searchParams.get('code'); // Код из ответа (режим разработки)
    
    if (emailParam) {
      setEmail(emailParam);
    }
    
    if (userIdParam) {
      setUserId(parseInt(userIdParam));
    }
    
    // Если есть код в URL (режим разработки), автоматически заполняем поле
    if (codeParam) {
      setCode(codeParam);
    }

    // Если нет ни email, ни userId - редирект на регистрацию
    if (!emailParam && !userIdParam) {
      navigate('/register');
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    // Таймер для повторной отправки (60 секунд)
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (code.length !== 6 || !/^\d+$/.test(code)) {
      setError('Код должен содержать 6 цифр');
      setLoading(false);
      return;
    }

    try {
      const result = await verifyEmail(userId || email, code);
      if (result.success) {
        setSuccess(true);
        // Редирект на главную через 2 секунды
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'Ошибка подтверждения кода');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || !email) return;

    setError('');
    setLoading(true);
    setCanResend(false);
    setResendCooldown(60);

    try {
      await resendVerificationCode(email);
      setError('');
      // Можно показать сообщение об успехе
    } catch (err) {
      setError(err.message || 'Ошибка отправки кода');
      setCanResend(true);
      setResendCooldown(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  return (
    <div className="email-verification-page">
      <div className="email-verification-container">
        <h1>Подтверждение email</h1>
        
        {success ? (
          <div className="success-message">
            <p>✅ Email успешно подтвержден!</p>
            <p>Вы будете перенаправлены на главную страницу...</p>
          </div>
        ) : (
          <>
            <p className="verification-info">
              Мы отправили код подтверждения на адрес <strong>{email || 'ваш email'}</strong>
            </p>
            {code && (
              <div className="dev-code-notice" style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '20px',
                textAlign: 'center',
                color: '#856404'
              }}>
                <strong>Режим разработки:</strong> Код был автоматически заполнен из ответа сервера
              </div>
            )}
            <p className="verification-hint">
              Введите 6-значный код из письма для завершения регистрации
            </p>

            <form onSubmit={handleSubmit} className="verification-form">
              <div className="form-group">
                <label htmlFor="code">Код подтверждения</label>
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  maxLength="6"
                  className={error ? 'error' : ''}
                  disabled={loading}
                  autoComplete="off"
                  autoFocus
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button 
                type="submit" 
                className="btn-primary"
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Проверка...' : 'Подтвердить'}
              </button>
            </form>

            <div className="resend-section">
              <p>Не получили код?</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={!canResend || loading || !email}
                className="btn-link"
              >
                {canResend 
                  ? 'Отправить код повторно' 
                  : `Отправить повторно через ${resendCooldown} сек.`
                }
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationPage;
